/**
 * One vital bar — and the correctness fix the whole panel is
 * built around.
 *
 * Health, stamina, hunger and thirst used to share one component that filled from the left as
 * the number rose. For two of them that is a lie with a body count attached: hunger 80 is not
 * "80% full", it is *about to die*, and the engine sets health to 0 when it reaches 100. The
 * number is left exactly as the engine reports it — the player and the log have to be able to
 * say `hunger 58` and mean the same thing — and the picture changes instead:
 *
 * | | fills from | colour |
 * |---|---|---|
 * | health, stamina | the left, draining toward empty | its own, fixed |
 * | hunger, thirst | **the right**, growing toward the wall it kills you at | **green → amber → red, by band** |
 *
 * Two bars moving in opposite directions is the point; the eye reads "long bar on the left" and
 * "long bar on the right" as different things before it reads a single word, which is what makes
 * the two 58s in the seeded character stop looking like the same news.
 *
 * **The band is the server's and the ramp reads it, not the number.** `band`, `band_label` and
 * `note` come display-joined from the API (U1) and the character panel forbids re-deriving them here, so the
 * colour switches where `rules.py` says it switches and nowhere else. `note` on tap is the
 * other half of that: *"Severe weakness, no natural healing above 70"* is the thing that makes
 * a roguelike read as deep rather than as random, and it is one line the API already sends.
 */
import { useId, useState } from 'react';
import type { VitalBand } from '@/api/generated';
import { polarityOf, vitalLabelKey, type VitalReading } from '@/features/character/vitals';
import { useT } from '@/i18n/context';

interface VitalBarProps {
  reading: VitalReading;
}

/**
 * Exhaustive over every band so a band the engine adds is a typecheck error rather than an
 * uncoloured bar (D5). Only the hunger and thirst bands are ever read — health and stamina
 * carry their own colour — but they are filled in honestly rather than left as a fallback.
 */
const BAND_RAMP: Record<VitalBand, 'ok' | 'warn' | 'danger'> = {
  fed: 'ok',
  very_hungry: 'warn',
  starving: 'danger',

  hydrated: 'ok',
  thirsty: 'warn',
  dehydrated: 'danger',

  healthy: 'ok',
  wounded: 'warn',
  badly_wounded: 'danger',
  critically_wounded: 'danger',
  dead: 'danger',

  rested: 'ok',
  tired: 'warn',
  fatigued: 'warn',
  exhausted: 'danger',
};

const RAMP_COLOR = {
  ok: 'var(--color-ramp-ok)',
  warn: 'var(--color-ramp-warn)',
  danger: 'var(--color-ramp-danger)',
};

export function VitalBar({ reading }: VitalBarProps) {
  const t = useT();
  const { name, vital, value, delta, pending, movedBy } = reading;
  const [open, setOpen] = useState(false);
  const noteId = useId();

  const polarity = polarityOf(name);
  const down = polarity === 'down';
  const label = t(vitalLabelKey(name));

  const color = down ? RAMP_COLOR[BAND_RAMP[vital.band]] : `var(--color-vital-${name})`;
  const hasNote = vital.note !== null;

  const head = (
    <>
      <span className="text-fg-muted">{label}</span>
      <span className="text-fg-faint truncate text-right">{vital.band_label}</span>
      <span className="text-fg tabular-nums">{value}</span>
    </>
  );

  return (
    <div className="relative py-1.5">
      {/* The flyout (the character panel's last checkbox): `−12` where the bar just moved, keyed on the
          change that moved it so a second hit in the same turn replays rather than freezes.
          It animates to invisible, which is also its resting state — under
          `prefers-reduced-motion` it therefore does not appear at all, and the ticker row it
          duplicates is still there saying the same thing in words. */}
      {delta !== null && pending ? (
        <span
          key={movedBy}
          aria-hidden="true"
          className="pointer-events-none absolute -top-1 right-0 font-mono motion-safe:animate-[vital-flyout_1400ms_ease-out_forwards]"
          style={{ fontSize: 'var(--text-meta)', color }}
        >
          {formatDelta(delta)}
        </span>
      ) : null}

      {hasNote ? (
        <button
          type="button"
          aria-expanded={open}
          aria-controls={open ? noteId : undefined}
          onClick={() => {
            setOpen(!open);
          }}
          className="hover:text-fg grid w-full grid-cols-[1fr_auto_2.5rem] items-baseline gap-x-3 text-left font-mono transition-colors"
          style={{ fontSize: 'var(--text-meta)' }}
        >
          {head}
        </button>
      ) : (
        <div
          className="grid w-full grid-cols-[1fr_auto_2.5rem] items-baseline gap-x-3 font-mono"
          style={{ fontSize: 'var(--text-meta)' }}
        >
          {head}
        </div>
      )}

      <div
        role="meter"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={value}
        // Without this a screen reader hears "58" and has to know the polarity to use it.
        aria-valuetext={`${value}, ${vital.band_label}`}
        className="bg-rule relative mt-1.5 h-1.5 w-full overflow-hidden"
      >
        <div
          className={`absolute inset-y-0 transition-[width] duration-500 ease-out ${down ? 'right-0' : 'left-0'}`}
          style={{ width: `${String(clamp(value))}%`, backgroundColor: color }}
        />
      </div>

      {open && vital.note !== null ? (
        <p id={noteId} className="text-fg-faint mt-2" style={{ fontSize: 'var(--text-meta)' }}>
          {vital.note}
        </p>
      ) : null}
    </div>
  );
}

/** U+2212, matching the engine's own summaries rather than a hyphen. */
function formatDelta(delta: number): string {
  return `${delta > 0 ? '+' : '−'}${String(Math.abs(delta))}`;
}

function clamp(value: number): number {
  return Math.max(0, Math.min(100, value));
}
